'use client'

import type { ReactNode } from 'react'
import Link from 'next/link'

import { useAuth } from '@/context/auth'
import { useAnalytics } from '@/hooks/useAnalytics'
import type { ImportProvider } from '@/lib/imports/types'

import { Button, type ButtonProps } from '@/components/ui/button'

type ImportLandingCtaProps = {
  children?: ReactNode
  className?: string
  importType?: 'memory' | 'conversations'
  label?: string
  provider?: ImportProvider
  size?: ButtonProps['size']
  surface?: string
  variant?: ButtonProps['variant']
}

function buildImporterHref(importType?: 'memory' | 'conversations', provider?: ImportProvider) {
  const params = new URLSearchParams({
    tab: 'imports',
    imports: '1'
  })

  if (importType) {
    params.set('importType', importType)
  }

  if (provider) {
    params.set('provider', provider)
  }

  return `/settings?${params.toString()}`
}

function buildLoginRedirectHref(destination: string) {
  return `/login?next=${encodeURIComponent(destination.replace(/^\//, ''))}`
}

export function ImportLandingCta({
  children,
  className,
  importType,
  label,
  provider,
  size = 'default',
  surface = 'unknown',
  variant = 'default'
}: ImportLandingCtaProps) {
  const { user } = useAuth()
  const { track } = useAnalytics()
  const importerHref = buildImporterHref(importType, provider)
  const href = user ? importerHref : buildLoginRedirectHref(importerHref)

  return (
    <Button asChild className={className} size={size} variant={variant}>
      <Link
        href={href}
        onClick={() => {
          track('import_landing_cta_clicked', {
            auth_state: user ? 'signed_in' : 'signed_out',
            import_type: importType ?? 'default',
            provider: provider ?? 'all',
            surface
          })
        }}
      >
        {children ?? label}
      </Link>
    </Button>
  )
}
